import { makeAutoObservable, runInAction } from "mobx";

import { teamMemberships } from "@/api";
import { MemberModel } from "@/models/MemberModel";
import { TeamModel } from "@/models/TeamModel";

import type { RootStore } from "@/store/RootStore";

type MembersStatus = "init" | "loading" | "loaded";

export class MembersUX {
  private rootStore: RootStore;

  membersStatus: MembersStatus = "init";

  team: TeamModel;

  members: MemberModel[] = [];

  private loadMembersPromise?: Promise<void>;

  constructor(rootStore: RootStore, team?: TeamModel) {
    this.rootStore = rootStore;
    if (!team) {
      // eslint-disable-next-line no-param-reassign
      team = new TeamModel(this.rootStore, {
        typename: "TeamShort",
        uid: "",
        title: "",
        bio: null,
        permalink: "",
        personal: false,
        kind: "regular",
        avatarUrls: null,
        followersCount: 0,
        unreadMessagesCount: 0,
        membersCount: 0,
        userPermissions: { read: true, write: true, invite: true, billing: true },
      });
    }
    this.team = team;
    makeAutoObservable<MembersUX, "loadMembersPromise">(this, { loadMembersPromise: false });
  }

  setMembersStatus(status: MembersStatus) {
    this.membersStatus = status;
  }

  get currentUserRole() {
    const username = this.rootStore.auth.currentUser?.username;
    return this.members.find((member) => member.user.username === username)?.role;
  }

  /**
   * Loads the members, sharing a request already in flight; a failed load leaves the status at
   * `init`. It takes no signal: one caller unmounting must not cancel the load for the others.
   */
  loadMembers(): Promise<void> {
    if (this.loadMembersPromise) return this.loadMembersPromise;

    const promise = this.fetchMembers().finally(() => {
      if (this.loadMembersPromise === promise) this.loadMembersPromise = undefined;
    });
    this.loadMembersPromise = promise;
    return promise;
  }

  private async fetchMembers() {
    this.setMembersStatus("loading");
    try {
      const { memberships } = await teamMemberships(this.team.permalink);
      if (memberships) {
        runInAction(() => {
          this.members = memberships.map((i) => new MemberModel(i, this.team));
        });
      }
      this.setMembersStatus("loaded");
    } catch (err) {
      this.setMembersStatus("init");
      throw err;
    }
  }
}
